/**
 * Which design an agency's public site is built in, answered as early as
 * it can be.
 *
 * Two exist: `classic`, the original Foundation agency site, and `estate`,
 * the design pack. It is a per-agency setting rather than a build flag,
 * because two agencies on the same server can be on different ones — which
 * is the only way to move a live business across without moving everybody.
 *
 * THE ORDER, AND WHY
 * ------------------
 *   1. `?theme=estate` / `?theme=off` on the URL. A link somebody was sent
 *      always wins, and it is how the design was reviewed before anybody
 *      was moved onto it.
 *   2. What this TAB was already showing, from sessionStorage. Clicking
 *      around a preview stays in the preview, and it does not follow you
 *      back into the real site tomorrow.
 *   3. The agency's own setting, from the brand.
 *   4. What this browser last resolved FOR THIS HOST, from localStorage.
 *   5. `classic`.
 *
 * Four exists because of when three arrives. `/api/brand` is a network
 * round trip — 45 to 130ms from the droplet, more on a phone — and the
 * page has already painted by then. Without a cached answer, the first
 * visit to a migrated site paints the old design and swaps, which is a
 * visible flip of the whole layout on somebody's homepage.
 *
 * It cannot be removed by caching harder: the first visit from a given
 * browser has nothing to go on. What it CAN be is once per browser rather
 * than once per page, which is what the cache buys. The same trade
 * `lib/marketBoot.js` makes, for the same reason, and bots are unaffected
 * either way because they are served real HTML by prerender.py.
 */
const CLASSIC = "classic";
export const ESTATE = "estate";
const VALID = [CLASSIC, ESTATE];

const SESSION_KEY = "fs_theme_preview";
const CACHE_PREFIX = "fs_site_template:";

function clean(value) {
  const v = String(value || "").trim().toLowerCase();
  return VALID.includes(v) ? v : null;
}

function host() {
  try {
    return window.location.hostname || "";
  } catch {
    return "";
  }
}

function readSession() {
  try {
    return clean(window.sessionStorage.getItem(SESSION_KEY));
  } catch {
    // Private windows and blocked site data both throw. A theme is not
    // worth an error boundary.
    return null;
  }
}

function writeSession(value) {
  try {
    if (value) window.sessionStorage.setItem(SESSION_KEY, value);
    else window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* no-op */
  }
}

function readCache() {
  try {
    return clean(window.localStorage.getItem(CACHE_PREFIX + host()));
  } catch {
    return null;
  }
}

/** Record what the brand actually said, so the next visit starts right. */
export function rememberSiteTemplate(value) {
  const v = clean(value);
  if (!v) return;
  try {
    window.localStorage.setItem(CACHE_PREFIX + host(), v);
  } catch {
    /* no-op */
  }
}

function fromQuery() {
  try {
    const asked = new URLSearchParams(window.location.search).get("theme");
    if (asked === null) return undefined;          // not asked at all
    const on = clean(asked) === ESTATE ? ESTATE : CLASSIC;
    // A URL choice is a preview: it sticks to this tab so browsing stays
    // in whichever one you are looking at.
    writeSession(on);
    return on;
  } catch {
    return undefined;
  }
}

/**
 * The template to render, given the brand (which may not have arrived).
 * Safe to call during render; it reads no React state.
 */
export function siteTemplate(brand) {
  if (typeof window === "undefined") {
    // Server-side. There is no query string to read, no sessionStorage and
    // no cache, so the agency's own setting is the whole answer — and it
    // has to be, because returning CLASSIC here would have the server draw
    // one design and the browser hydrate another, which throws the server's
    // HTML away entirely. The browser reaches the same answer by the same
    // route for anyone who has not typed ?theme= : the query and the
    // session are both empty, so it falls to brand.site_template too.
    return clean(brand && brand.site_template) || CLASSIC;
  }
  const asked = fromQuery();
  if (asked !== undefined) return asked;
  const session = readSession();
  if (session) return session;
  return clean(brand && brand.site_template) || readCache() || CLASSIC;
}

/** Convenience: the question every page actually asks. */
export function isEstate(brand) {
  return siteTemplate(brand) === ESTATE;
}

/**
 * Whether what is on screen is a PREVIEW rather than the agency's site.
 *
 * True when the URL or this tab put us in the new design and the agency
 * has not been moved onto it. The floating "Preview" badge keys off this:
 * once an agency's own setting says estate, that IS their website, and a
 * badge telling their customers they are looking at a preview of it would
 * be wrong in front of the people it matters most to.
 */
export function isPreviewing(brand) {
  if (typeof window === "undefined") return false;
  if (!isEstate(brand)) return false;
  return clean(brand && brand.site_template) !== ESTATE;
}

/** Keep a preview flag on a link, so browsing the preview stays in it. */
export function withPreview(href, on) {
  if (!on || !href) return href;
  const [path, hash = ""] = String(href).split("#");
  const join = path.includes("?") ? "&" : "?";
  return `${path}${join}theme=${ESTATE}${hash ? `#${hash}` : ""}`;
}
